import React, { useEffect, useRef } from "react";
import styles from "./index.module.scss";
import ButtonC from "../../../button";
import moment from "moment";
import { useNotificationsInfinite } from "../../../../../hooks/react-query/calling-pipeline/useNotificationsInfinite";
import { useMarkNotificationRead } from "../../../../../hooks/react-query/calling-pipeline/useMarkNotificationRead";
import { useMarkAllNotificationsRead } from "../../../../../hooks/react-query/calling-pipeline/useMarkAllNotificationsRead";
import { monthFormate } from "../../../../../utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import SkeletonC from "../../../skeleton";
import { useUnreadNotificationsCount } from "../../../../../hooks/react-query/calling-pipeline/useUnreadNotificationsCount";

export default function NotificationBody({
  setShowModel
}: {
  setShowModel?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useNotificationsInfinite();
  const { data: counts, refetch } = useUnreadNotificationsCount();
  const notifications =
    data?.pages?.flatMap((page: any) => page?.data ?? []) ?? [];
  // const totalCount = data?.pages?.[0]?.total ?? 0;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const queryKey = [
    "calling-pipeline-notifications-infinite",
    searchParams.toString()
  ];
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    const sentinel = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => {
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();

  const handleMarkAsRead = (e: React.MouseEvent, notificationId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!notificationId) return;
    // Optimistic UI update via query cache
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.map((n: any) =>
            n.notificationId === notificationId ? { ...n, isRead: true } : n
          )
        }))
      };
    });
    markRead(
      { notificationId },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          // Revert by invalidating so it refetches
          queryClient.invalidateQueries({ queryKey });
        }
      }
    );
  };
  return (
    <div className={styles.NotificationBody}>
      <div className={styles.header}>
        <img
          src="/images/arrow-left.svg"
          alt=""
          className={styles.arrow}
          onClick={() => {
            if (setShowModel) {
              setShowModel(false);
            }
          }}
        />
        <div className={styles.title}>
          Notifications{" "}
          <span className={styles.count}>{counts?.data?.count || 0}</span>
        </div>
        <ButtonC
          type="button"
          btnType="link"
          label={"Marked All As Read"}
          onClick={() => {
            // Optimistically update all to read
            queryClient.setQueryData(queryKey, (oldData: any) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => ({
                  ...page,
                  data: page.data.map((n: any) => ({ ...n, isRead: true }))
                }))
              };
            });
            markAllRead(undefined, {
              onSuccess: () => {
                refetch();
              },
              onError: () => {
                // Revert by invalidating so it refetches
                queryClient.invalidateQueries({ queryKey });
              }
            });
          }}
          className={styles.markAllAsRead}
          tabIndex={25}
        />
      </div>
      <div className={styles.timeline}>
        {isLoading && (
          <>
            {[1, 2, 3, 4, 5, 6, 7].map((key) => (
              <div key={key} className={styles.timelineItem}>
                <div
                  className={styles.scheduledCall}
                  style={{ marginBottom: "2px" }}
                >
                  <SkeletonC width="180px" height="12px" />
                </div>
                <div className={styles.contactNameWrap}>
                  <span className={styles.contactName}>
                    <SkeletonC width="120px" height="18px" />
                  </span>
                  <span className={styles.date}>
                    <div className={styles.dot}></div>
                    <SkeletonC width="80px" height="16px" />
                  </span>
                </div>
                <div className={styles.address}>
                  <SkeletonC width="220px" height="16px" />
                </div>
              </div>
            ))}
          </>
        )}
        {notifications.map((item: any) => (
          <div
            className={styles.timelineItem}
            key={item.notificationId || item.id}
            onClick={(e) => {
              if (!item.isRead) {
                handleMarkAsRead(e, item.notificationId);
              }
            }}
            style={{ opacity: item.isRead ? 0.6 : 1 }}
          >
            {item?.followupDate && (
              <div className={styles.scheduledCall}>
                {moment.utc(item?.followupDate).local().isBefore(moment()) ? (
                  <span className={styles.followupDue}>
                    Follow-up Due on{" "}
                    {moment.utc(item?.followupDate).local().format("MMM Do")}
                  </span>
                ) : (
                  <span className={styles.followupScheduled}>
                    Follow-up Scheduled on{" "}
                    {moment.utc(item?.followupDate).local().format("MMM Do")}
                  </span>
                )}
              </div>
            )}
            <div className={styles.contactNameWrap}>
              <span className={styles.contactName}>{item.contactName}</span>
              <span className={styles.date}>
                <div className={styles.dot}></div>
                {monthFormate(item.createdAt)}
              </span>
            </div>
            <div className={styles.address}>{item.address}</div>
          </div>
        ))}
        <div ref={loadMoreRef} aria-hidden className={styles.markAllAsRead} />
      </div>
      {/* <div className={styles.NoNotifications}>No Notifications Found</div> */}
    </div>
  );
}
